import { __, declension } from '../../utils';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

const PAGE = 6;

type Props = {
  answers: string[];
  /** Bez hľadania a bez čísel - občan si otvorené odpovede len číta. */
  compact?: boolean;
};

/**
 * Otvorené odpovede **v karte otázky**, nie v samostatnej tabuľke pod ňou.
 * Predtým to boli podriadky tabuľky s počtami, kde sa medzi číslami stratili;
 * pritom je to jediná časť výsledku, ktorú treba čítať, nie porovnávať.
 *
 * Hľadanie je tu **na klientovi a je to v poriadku** - na rozdiel od hľadania
 * v zoznamoch. `getVotingResults` vracia `text_answers` celé, takže sa
 * prehľadáva úplná množina, nie načítaná strana. Rovnaké pole ide aj do
 * exportu, preto pri ňom stojí poznámka „v exporte sú všetky".
 */
const TextAnswers: React.FC<Props> = ({ answers, compact = false }) => {
  const [query, setQuery] = React.useState('');
  const [visible, setVisible] = React.useState(PAGE);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('sk');
    if (!needle) return answers;
    return answers.filter((answer) =>
      answer.toLocaleLowerCase('sk').includes(needle),
    );
  }, [answers, query]);

  const shown = filtered.slice(0, visible);
  const rest = filtered.length - shown.length;

  if (!answers.length) return null;

  return (
    <Stack
      gap={1.75}
      sx={({ palette, shape }) =>
        compact
          ? {}
          : {
              border: `1px solid ${palette.divider}`,
              borderRadius: `${shape.borderRadius}px`,
              backgroundColor: palette.grey[50],
              p: 2.5,
            }
      }
    >
      {!compact && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1.5}
        >
          <Typography component='b' sx={{ fontSize: 15, fontWeight: 700 }}>
            {`${answers.length} ${declension(answers.length, [
              __('otvorená odpoveď'),
              __('otvorené odpovede'),
              __('otvorených odpovedí'),
            ])}`}
          </Typography>

          <TextField
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setVisible(PAGE);
            }}
            placeholder={__('Hľadať v odpovediach')}
            sx={{ flex: 1, maxWidth: { sm: 320 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ ml: { sm: 'auto' }, flex: 'none' }}
          >
            {__('v exporte sú všetky')}
          </Typography>
        </Stack>
      )}

      {shown.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          {__('Hľadanému výrazu nezodpovedá žiadna odpoveď.')}
        </Typography>
      ) : (
        <Box
          sx={({ palette, shape }) => ({
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: palette.background.paper,
            border: compact ? 'none' : `1px solid ${palette.divider}`,
            borderRadius: `${shape.borderRadius}px`,
            px: compact ? 0 : 2,
          })}
        >
          {shown.map((answer, index) => (
            <Stack
              key={`${index}-${answer.slice(0, 24)}`}
              direction='row'
              gap={1.75}
              sx={({ palette }) => ({
                py: 1.5,
                borderBottom: `1px solid ${palette.dividerLight}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              {!compact && (
                <Typography
                  component='span'
                  sx={({ typography, palette }) => ({
                    flex: 'none',
                    width: 24,
                    fontFamily: typography.fontFamilyMono,
                    fontSize: 13,
                    color: palette.grey[400],
                  })}
                >
                  {index + 1}
                </Typography>
              )}
              <Typography sx={{ flex: 1, fontSize: 15, lineHeight: 1.55 }}>
                {answer}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}

      {rest > 0 && (
        <Button
          variant='outline'
          size='small'
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => setVisible((current) => current + PAGE * 4)}
        >
          {`${__('Zobraziť ďalších')} ${rest}`}
        </Button>
      )}
    </Stack>
  );
};

export default TextAnswers;
